
import { Component, OnChanges, Output, Input, EventEmitter, SimpleChanges } from '@angular/core';
import { SortedTableClass, IAction, ActionType, DateFormatPipe, TableRowEmptyComponent } from '@aston/foundation';
import { NgClass, UpperCasePipe } from '@angular/common';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

import { IAccountInformationModel, ISubsidiaryUser, RoleLike } from '../../models';
import { UserRole, UserStatus, UsersListSortProp } from '../../enums';
import { MiddleUsersStatusActionsComponent } from '../middle-users-status-actions/middle-users-status-actions.component';

const allCols = {
	firstName: true,
	lastName: true,
	userName: true,
	email: false,
	subsidiaryRole: true,
	delegation: true,
	lastConnection: true,
	actions: false,
};

@Component({
    selector: 'middle-users-list-table',
    styleUrl: './middle-users-list-table.component.scss',
    imports: [
        NgClass,
        TranslateModule,
        NgbTooltipModule,
        NgbDropdownModule,
        DateFormatPipe,
        UpperCasePipe,
        TableRowEmptyComponent,
        MiddleUsersStatusActionsComponent,
    ],
    templateUrl: './middle-users-list-table.component.html'
})
export class MiddleUsersListTableComponent extends SortedTableClass<ISubsidiaryUser, UsersListSortProp> implements OnChanges {
	@Input({ required: true }) roles!: UserRole[];
	@Input({ required: true }) currentUser!: IAccountInformationModel;
	@Input() columns: Partial<typeof allCols> = allCols;
	/**
	 * Emitted to change a user's status
	 */
	@Output() status = new EventEmitter<IAction<{ userId: number, userStatus: UserStatus }>>();
	/**
	 * Emitted to change a user's role
	 */
	@Output() role = new EventEmitter<IAction<{ userId: number, subsidiaryRole: RoleLike }>>();
	/**
	 * Emitted to resend the invitation mail
	 */
	@Output() registrationRequest = new EventEmitter<IAction<{ userId: number }>>();
	/**
	 * Emitted to view user delegation details
	 */
	@Output() openDelegationView = new EventEmitter<IAction<{ user: ISubsidiaryUser }>>();
	/**
	 * Emitted to open the delegation form
	 */
	@Output() openDelegationForm = new EventEmitter<IAction<{ user: ISubsidiaryUser, currentUser: ISubsidiaryUser }>>();
	/**
	 * Emitted to open the inactivity dialog
	 */
	@Output() openInactivityForm = new EventEmitter<IAction<{ user: ISubsidiaryUser, currentUser: ISubsidiaryUser }>>();
	/**
	 * Emitted to open the inactivity confirmation
	 */
	@Output() toggleInactivity = new EventEmitter<IAction<{ user: ISubsidiaryUser, currentUser: ISubsidiaryUser, toActive: boolean }>>();
	/**
	 * Emitted to open the deletion confirmation
	 */
	@Output() openDeletionForm = new EventEmitter<IAction<{ user: ISubsidiaryUser, currentUser: ISubsidiaryUser }>>();

	sortCols = UsersListSortProp;
	userStatus = UserStatus;

	userRoles = UserRole;
	currentSubsidiaryUser!: ISubsidiaryUser;

	ngOnChanges(changes: SimpleChanges) {
		if (this.currentUser && changes.list) {
			this.currentSubsidiaryUser = this.list.items.find(u => u.userId === this.currentUser.id) as ISubsidiaryUser;
		}
	}

	changeUserStatus(userId: number, userStatus: UserStatus) {
		this.status.emit({type: ActionType.UPDATE, value: {userId, userStatus}});
	}

	changeSubsidiaryRole(userId: number, subsidiaryRole: UserRole) {
		this.role.emit({type: ActionType.UPDATE, value: {userId, subsidiaryRole}});
	}

	resendRegistrationRequest(userId: number) {
		this.registrationRequest.emit({type: ActionType.UPDATE, value: {userId}});
	}

	roleUnknown(role: UserRole) {
		return !Object.values(this.userRoles).includes(role);
	}

	onOpenDelegationView(user: ISubsidiaryUser) {
		this.openDelegationView.emit({type: ActionType.UPDATE, value: {user}});
	}

	onOpenDelegationForm(user: ISubsidiaryUser) {
		this.openDelegationForm.emit({type: ActionType.UPDATE, value: {user, currentUser: this.currentSubsidiaryUser}});
	}

	onOpenInactivityForm(user: ISubsidiaryUser) {
		this.openInactivityForm.emit({type: ActionType.UPDATE, value: {user, currentUser: this.currentSubsidiaryUser}});
	}

	onToggleInactivity(user: ISubsidiaryUser, toActive: boolean) {
		this.toggleInactivity.emit({type: ActionType.UPDATE, value: {user, currentUser: this.currentSubsidiaryUser, toActive}});
	}

	onOpenDeletionForm(user: ISubsidiaryUser) {
		this.openDeletionForm.emit({type: ActionType.UPDATE, value: {user, currentUser: this.currentSubsidiaryUser}});
	}
}
